import { TFunction } from 'i18next';
import { z } from 'zod';

import { KpiType, ProjectType } from '@/shared/types/api';
import { KpiUnit } from '@/shared/types/kpi';
export type KpiVariant = 'standard' | 'derivative';
// Math operation enum to match the database enum
export const MathOperation = z.enum(['add', 'subtract', 'multiply', 'divide']);

export const getKpiVariant = (projectType: ProjectType) => {
  if (projectType === 'subproject_contractor') {
    return 'derivative';
  }
  return 'standard';
};

// Base KPI schema shared between standard and derivative KPIs
const baseKpiSchema = (t: TFunction<'project', 'KpiFormEditor'>) =>
  z.object({
    name: z
      .string({ message: t('form_field.name.errors.required') })
      .min(1, { message: t('form_field.name.errors.required') }),
    type: z.nativeEnum(KpiType, {
      message: t('form_field.type.errors.required'),
    }),
    max_value: z.number().optional(),
    unit: z.string().refine((value) => KpiUnit.fromString(value), {
      message: t('form_field.unit.errors.required'),
    }),
  });

// Standard KPI schema
const standardKpiSchema = (t: TFunction<'project', 'KpiFormEditor'>) =>
  baseKpiSchema(t).extend({
    variant: z.literal('standard'),
  });

// Derivative KPI schema without validation (validation moved to main schema)
const derivativeKpiSchema = (t: TFunction<'project', 'KpiFormEditor'>) =>
  baseKpiSchema(t).extend({
    variant: z.literal('derivative'),
    kpi_operand_1: z.number().optional(),
    kpi_operand_2: z.number().optional(),
    custom_operand_1: z.number().optional(),
    custom_operand_2: z.number().optional(),
    operation: MathOperation,
  });

// Infer type for validation helpers
type DerivativeKpiData = z.infer<ReturnType<typeof derivativeKpiSchema>>;

// Helper function to validate operands for derivative KPIs
const validateDerivativeOperands = (
  data: DerivativeKpiData,
  ctx: z.RefinementCtx,
  t: TFunction<'project', 'KpiFormEditor'>,
  operandNumber: 1 | 2,
) => {
  const kpiOperandKey = `kpi_operand_${operandNumber}` as const;
  const customOperandKey = `custom_operand_${operandNumber}` as const;
  const hasKpiOperand = data[kpiOperandKey] !== undefined;
  const hasCustomOperand = data[customOperandKey] !== undefined;

  // An operand is valid if it's either a KPI or a custom value, but not both.
  // This XOR check simplifies the logic.
  if (hasKpiOperand === hasCustomOperand) {
    const message = hasKpiOperand
      ? t(`form_field.operand_${operandNumber}.errors.exclusive`)
      : t(`form_field.operand_${operandNumber}.errors.required`);
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: [kpiOperandKey],
    });
  }
};

// Helper function to validate division by zero
const validateDivisionByZero = (
  data: DerivativeKpiData,
  ctx: z.RefinementCtx,
  t: TFunction<'project', 'KpiFormEditor'>,
) => {
  if (data.operation === 'divide' && data.custom_operand_2 === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t('form_field.operand_2.errors.division_by_zero'),
      path: ['custom_operand_2'],
    });
  }
};

// Helper function to validate self-reference
const validateSelfReference = (
  data: DerivativeKpiData,
  ctx: z.RefinementCtx,
  t: TFunction<'project', 'KpiFormEditor'>,
) => {
  if (
    data.kpi_operand_1 &&
    data.kpi_operand_2 &&
    data.kpi_operand_1 === data.kpi_operand_2
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t('form_field.operand_2.errors.self_reference'),
      path: ['kpi_operand_2'],
    });
  }
};

const validateAtLeastOneOperand = (
  data: DerivativeKpiData,
  ctx: z.RefinementCtx,
  t: TFunction<'project', 'KpiFormEditor'>,
) => {
  if (!data.kpi_operand_1 && !data.kpi_operand_2) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t('form_field.operand_1.errors.at_least_one_operand'),
      path: ['kpi_operand_1'],
    });
  }
};

export const kpiEditorSchema = (t: TFunction<'project', 'KpiFormEditor'>) =>
  z
    .discriminatedUnion('variant', [
      standardKpiSchema(t),
      derivativeKpiSchema(t),
    ])
    .superRefine((data, ctx) => {
      if (data.variant === 'derivative') {
        validateDerivativeOperands(data, ctx, t, 1);
        validateDerivativeOperands(data, ctx, t, 2);
        validateDivisionByZero(data, ctx, t);
        validateSelfReference(data, ctx, t);
        validateAtLeastOneOperand(data, ctx, t);
      }
    });

export type KpiEditorSchemaInput = z.output<ReturnType<typeof kpiEditorSchema>>;
